import { useLanguage } from '@/components/Link'
import { dateIsInDates, getDay } from '@/components/page-specific/catering/common/helpers/date'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { getMinDateAllowed } from '@caterdesk/utils--gm-validation'
import CloseRounded from '@mui/icons-material/CloseRounded'
import { Box, IconButton } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { addMonths } from 'date-fns/addMonths'
import { de } from 'date-fns/locale/de'
import { enGB } from 'date-fns/locale/en-GB'
import { it } from 'date-fns/locale/it'
import { nl } from 'date-fns/locale/nl'

import { useVendorLocationData } from '../../store/vendorLocationStore'

export interface DatePickerInputProps {
  minDate?: Date
  forceClose?: boolean
  clearable?: boolean
  showErrors?: boolean
  showAllDates?: boolean
  showChevron?: boolean
  dateValue?: number
  disabled?: boolean
  error?: string
  onChange: (newDate: number) => void
  onClear?: () => void
}

export function DatePickerInput({
  minDate,
  showErrors = true,
  showAllDates = false,
  dateValue,
  error,
  onClear,
  onChange,
}: DatePickerInputProps) {
  const { availableVendorLocations, selectedVendorLocation } = useVendorLocationData()
  const locationsToUse = showAllDates ? [] : availableVendorLocations
  const language = useLanguage()
  const datePickerLang =
    language === 'nl' ? nl : language === 'de' ? de : language === 'it' ? it : enGB

  const handleChange = (newDate: Date) => {
    onChange(newDate.getTime())
  }

  const closedWeekdays = ((locationsToUse || [])[0]?.closingDates?.closedWeekdays || []).filter(
    (weekday) =>
      locationsToUse?.every((location) => location.closingDates?.closedWeekdays?.includes(weekday)),
  )
  const closedDates = ((locationsToUse || [])[0]?.closingDates?.closedDates || []).filter((date) =>
    locationsToUse?.every((location) => location.closingDates?.closedDates?.includes(date)),
  )

  const cutOff5pm = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)
  const maxDateAllowed = addMonths(new Date(), 4)
  const leadTimeHours =
    selectedVendorLocation?.details?.effectiveMinimumNoticePeriod ??
    locationsToUse?.[0]?.details?.effectiveMinimumNoticePeriod ??
    null
  const leadTimeDate =
    typeof leadTimeHours === 'number' && Number.isFinite(leadTimeHours)
      ? new Date(Date.now() + leadTimeHours * 60 * 60 * 1000)
      : null

  const computedMinDate = getMinDateAllowed(locationsToUse, { date: dateValue }, cutOff5pm)
  const minDateAllowed = (() => {
    if (minDate) return minDate
    if (leadTimeDate && leadTimeDate > computedMinDate) return leadTimeDate
    return computedMinDate
  })()

  const filterDates = (date: Date) => {
    if (closedWeekdays.find((day) => day === getDay(date))) return false
    return !dateIsInDates(date, closedDates)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLang}>
      <DatePicker
        onChange={(date) => (date ? handleChange(new Date(date.valueOf())) : null)}
        value={dateValue ? new Date(dateValue) : null}
        minDate={minDateAllowed}
        maxDate={maxDateAllowed}
        shouldDisableDate={(date) => !filterDates(new Date(date.valueOf()))}
        desktopModeMediaQuery={'@media (min-height: 650px'}
        slotProps={{
          textField: {
            error: showErrors,
            helperText: showErrors ? error : '',
            fullWidth: true,
            size: 'small',
            sx: {
              '& .MuiIconButton-root': {
                color: 'primary.main',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 4,
              },
            },
            inputProps: {
              'data-cy': 'deliveryDate',
            },
          },
        }}
      />

      {onClear && dateValue && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: '10px',
          }}>
          <IconButton
            color="warning"
            size="small"
            onClick={() => {
              onClear()
            }}
            sx={{ marginRight: -1, marginTop: 0.7 }}>
            <CloseRounded />
          </IconButton>
        </Box>
      )}
    </LocalizationProvider>
  )
}
